import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

export default function ResetPassword() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError(t("rp.mismatch")); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0C10] px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center">
          <img src="/logo.png" alt="Golden Terminal" className="mb-3 h-14 w-14 object-contain" />
          <h1 className="font-heading text-2xl font-bold text-white">{t("rp.title")}</h1>
        </div>
        <div className="rounded-2xl border border-slate-800 bg-[#0E1118] p-6 shadow-2xl">
          {!token ? (
            <p className="text-center text-sm text-red-400">{t("rp.invalid")}</p>
          ) : done ? (
            <div className="text-center" data-testid="reset-done">
              <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-400" />
              <p className="text-sm text-slate-300">{t("rp.done")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-500" />
                <input data-testid="reset-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("rp.newPh")}
                  className="w-full rounded-lg border border-slate-700 bg-[#11141A] py-2.5 pl-10 pr-3 text-sm text-white placeholder-slate-500 outline-none focus:border-blue-500" />
              </div>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-500" />
                <input data-testid="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t("rp.confirmPh")}
                  className="w-full rounded-lg border border-slate-700 bg-[#11141A] py-2.5 pl-10 pr-3 text-sm text-white placeholder-slate-500 outline-none focus:border-blue-500" />
              </div>
              {error && <p data-testid="reset-error" className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-400">{error}</p>}
              <button data-testid="reset-submit" type="submit" disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("rp.submit")}
              </button>
            </form>
          )}
        </div>
        <Link to="/login" className="mt-5 block text-center text-sm text-slate-500 hover:text-slate-300">{t("rp.back")}</Link>
      </div>
    </div>
  );
}
